import i18nFactory from '@yandex-int/i18n';
import { keys, mapValues, omit } from 'lodash';
import moment from 'moment';

import {
    FetchUserAccountInfoRequest,
    FetchUserAccountsInfoRequest
} from '../../api/CommonDataProvider';
import { castStringToEnum } from '../../service/CastHelpers';
import { getNumberValue } from '../../service/FormatHelpers/FormatHelpers';
// import * as currenciesI18N from ' b:currencies t:i18n';
import { WalletDayBudgetValue } from '../../typings/budget';
import {
    BrandSurveyStatus,
    BrandSurveyStopReason,
    SurveyStatus
} from '../../typings/campaigns';
import {
    AccessRights,
    AuthorizationData,
    ClientProperties,
    DateRangePreset,
    OfficeApiContact,
    OfficeApiContacts,
    OfficeContact,
    OfficeInfo,
    UserAccountInfo,
    UserAccountsInfo,
    WalletData
} from '../../typings/common';
import {
    AdvType,
    BannersAdditionalFilter,
    CampaignPlatform,
    CampaignsAdditionalFilter,
    CampaignsCalcTypeFilter,
    CampaignType,
    GroupsAdditionalFilter,
    GroupType,
    ScreenAdditionalFilter,
    Stats
} from '../../typings/domain';
import {
    AdGroupType,
    AuthorizationQuery,
    CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus,
    CommonQuery,
    CommonQuery_client,
    CommonQuery_client_info_agencyInfo,
    CurrencyCode,
    GdAdditionalTargetingJoinType,
    GdAdditionalTargetingJoinTypeInput,
    GdAdditionalTargetingMode,
    GdAdditionalTargetingModeInput,
    GdAdGroupType,
    GdAdPriceCurrency,
    GdBrandSurveyStopReason,
    GdCalcTypeInput,
    GdCampaignBudgetShowMode,
    GdCampaignPlatform,
    GdCampaignType,
    GdStatPresetInput,
    GdSurveyStatus,
    GdWalletBudgetShowMode,
    GdWalletBudgetType,
    GroupTypeInput,
    statsDataFragment,
    WalletsDataFragment
} from '../../typings/graphql-grid';
import {
    BannersAction,
    CampaignsAction,
    GridAction,
    GridTotalStatsInfo,
    GroupsAction,
    SortParams
} from '../../typings/grid';
import { AgencyInfo } from '../../typings/state';

import * as keyset from './common.i18n';

const currenciesI18N = i18nFactory(keyset);

export function getValueFromApiFormat<T>(value: T | null): T | undefined {
    return value === null ? undefined : value;
}

export function getNumberFromApiFormat(
    value: string | null
): number | undefined {
    return value === null ? undefined : Number(value);
}

export function getStringFromApiFormat(
    value: number | string | null
): string | undefined {
    return value === null ? undefined : String(value);
}

export function mapNumberStringToApiFormat(value: string): string | null {
    return value === '' ? null : value.split(',').join('.');
}

export function filterArrayByNullAndUndefined<T>(
    array: Array<T | null | undefined>
): Array<T> {
    // user-defined type guards: https://www.typescriptlang.org/docs/handbook/advanced-types.html
    return array.filter((x): x is T => x !== null && x !== undefined);
}

export function mapStringToDate(date: string): moment.Moment {
    return moment.utc(date);
}

export function mapDateToApiFormat(date: moment.Moment): string {
    return date.format('YYYY-MM-DD');
}

export function mapMetrikaCountersToApiFormat(
    metrikaCounters: string[]
): number[] {
    return metrikaCounters.map(counter => +counter);
}

export function mapMetrikaCountersFromApiFormat(
    metrikaCounters: number[] | null
): string[] {
    return metrikaCounters ? metrikaCounters.map(String) : [];
}

const workCurrencies: Record<CurrencyCode, string> = {
    [CurrencyCode.RUB]: currenciesI18N('RUB'),
    [CurrencyCode.KZT]: currenciesI18N('KZT'),
    [CurrencyCode.BYN]: currenciesI18N('BYN'),
    [CurrencyCode.CHF]: currenciesI18N('CHF'),
    [CurrencyCode.EUR]: currenciesI18N('EUR'),
    [CurrencyCode.TRY]: currenciesI18N('TRY'),
    [CurrencyCode.UAH]: currenciesI18N('UAH'),
    [CurrencyCode.USD]: currenciesI18N('USD'),
    [CurrencyCode.YND_FIXED]: currenciesI18N('YND_FIXED')
};

const adPriceCurrencies: Record<GdAdPriceCurrency, string> = {
    [GdAdPriceCurrency.BYN]: currenciesI18N('BYN'),
    [GdAdPriceCurrency.CHF]: currenciesI18N('CHF'),
    [GdAdPriceCurrency.EUR]: currenciesI18N('EUR'),
    [GdAdPriceCurrency.KZT]: currenciesI18N('KZT'),
    [GdAdPriceCurrency.RUB]: currenciesI18N('RUB'),
    [GdAdPriceCurrency.TRY]: currenciesI18N('TRY'),
    [GdAdPriceCurrency.UAH]: currenciesI18N('UAH'),
    [GdAdPriceCurrency.USD]: currenciesI18N('USD'),
    [GdAdPriceCurrency.UZS]: currenciesI18N('UZS')
};

export function mapStringToCurrencySymbol(currencyString: string): string {
    if (CurrencyCode[currencyString]) {
        return workCurrencies[currencyString];
    }
    if (GdAdPriceCurrency[currencyString]) {
        return adPriceCurrencies[currencyString];
    }
    throw new Error(`Для значения ${currencyString} не определено название`);
}

export function mapCampaignRowTypeToCampaignType(
    campaignTypeString: GdCampaignType
): CampaignType {
    const dictionary = {
        [GdCampaignType.TEXT]: CampaignType.TEXT,
        [GdCampaignType.PERFORMANCE]: CampaignType.PERFORMANCE,
        [GdCampaignType.DYNAMIC]: CampaignType.DYNAMIC,
        [GdCampaignType.MCBANNER]: CampaignType.MCBANNER,
        [GdCampaignType.MOBILE_CONTENT]: CampaignType.MOBILE_CONTENT,
        [GdCampaignType.CPM_BANNER]: CampaignType.CPM_BANNER,
        [GdCampaignType.CPM_DEALS]: CampaignType.CPM_DEALS,
        [GdCampaignType.CPM_YNDX_FRONTPAGE]: CampaignType.CPM_YNDX_FRONTPAGE,
        [CampaignType.INTERNAL_DISTRIB]: CampaignType.INTERNAL_DISTRIB,
        [CampaignType.INTERNAL_FREE]: CampaignType.INTERNAL_FREE
    };

    if (!dictionary[campaignTypeString]) {
        throw new Error(`Тип кампании ${campaignTypeString} не поддерживается`);
    }

    return dictionary[campaignTypeString];
}

export function mapBrandSurveyStatusRowToBrandSurveyStatus(
    brandSurveyStatusRow: CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus | null
): BrandSurveyStatus | null {
    if (brandSurveyStatusRow === null) {
        return null;
    }

    const {
        surveyStatus,
        reasonIds,
        brandSurveyStopReasons
    } = brandSurveyStatusRow;

    return {
        surveyStatus: mapSurveyStatusRowToSurveyStatus(surveyStatus),
        reasonIds: reasonIds.map(String),
        brandSurveyStopReasons: brandSurveyStopReasons.map(
            mapUnfeasibleReasonsRowToUnfeasibleReasons
        )
    };
}

export function mapUnfeasibleReasonsRowToUnfeasibleReasons(
    brandSurveyStopReasons: GdBrandSurveyStopReason
): BrandSurveyStopReason {
    const mapper: Record<GdBrandSurveyStopReason, BrandSurveyStopReason> = {
        [GdBrandSurveyStopReason.LOW_BUDGET]: BrandSurveyStopReason.LOW_BUDGET,
        [GdBrandSurveyStopReason.LOW_REACH]: BrandSurveyStopReason.LOW_REACH
    };

    if (mapper[brandSurveyStopReasons]) {
        return mapper[brandSurveyStopReasons];
    }

    throw new Error(
        `unfeasible reason ${brandSurveyStopReasons} is not defined`
    );
}

export function mapSurveyStatusRowToSurveyStatus(
    surveyStatus: GdSurveyStatus
): SurveyStatus {
    const mapper: Record<GdSurveyStatus, SurveyStatus> = {
        [GdSurveyStatus.ACTIVE]: SurveyStatus.ACTIVE,
        [GdSurveyStatus.COMPLETED]: SurveyStatus.COMPLETED,
        [GdSurveyStatus.MODERATION_REJECTED]: SurveyStatus.MODERATION_REJECTED,
        [GdSurveyStatus.DRAFT]: SurveyStatus.DRAFT,
        [GdSurveyStatus.MODERATION]: SurveyStatus.MODERATION,
        [GdSurveyStatus.UNFEASIBLE]: SurveyStatus.UNFEASIBLE
    };

    if (mapper[surveyStatus]) {
        return mapper[surveyStatus];
    }

    throw new Error(`Survey status ${surveyStatus} for text is not defined`);
}

export function mapGroupTypeToGroupTypeInput(type: GroupType): GroupTypeInput {
    const dictionary = {
        [GroupType.CPM_BANNER]: GroupTypeInput.BANNER,
        [GroupType.CPM_AUDIO]: GroupTypeInput.AUDIO,
        [GroupType.CPM_INDOOR]: GroupTypeInput.INDOOR,
        [GroupType.CPM_OUTDOOR]: GroupTypeInput.OUTDOOR,
        [GroupType.CPM_GEOPRODUCT]: GroupTypeInput.BANNER_IN_METRO,
        [GroupType.CPM_VIDEO]: GroupTypeInput.VIDEO,
        [GroupType.CPM_YNDX_FRONTPAGE]: GroupTypeInput.MAIN_PAGE
    };

    if (!dictionary[type]) {
        throw new Error(`Тип группы ${type} не поддерживается`);
    }

    return dictionary[type];
}

export function mapGroupRowTypeToGroupType(
    groupTypeString: GdAdGroupType
): GroupType {
    const dictionary = {
        [GdAdGroupType.TEXT]: GroupType.TEXT,
        [GdAdGroupType.PERFORMANCE]: GroupType.PERFORMANCE,
        [GdAdGroupType.DYNAMIC]: GroupType.DYNAMIC,
        [GdAdGroupType.MCBANNER]: GroupType.MCBANNER,
        [GdAdGroupType.MOBILE_CONTENT]: GroupType.MOBILE_CONTENT,
        [GdAdGroupType.CPM_BANNER]: GroupType.CPM_BANNER,
        [GdAdGroupType.CPM_VIDEO]: GroupType.CPM_VIDEO,
        [GdAdGroupType.CPM_AUDIO]: GroupType.CPM_AUDIO,
        [GdAdGroupType.INTERNAL]: GroupType.INTERNAL,
        [GdAdGroupType.CPM_INDOOR]: GroupType.CPM_INDOOR,
        [GdAdGroupType.CPM_OUTDOOR]: GroupType.CPM_OUTDOOR,
        [GdAdGroupType.CPM_GEOPRODUCT]: GroupType.CPM_GEOPRODUCT
    };

    if (!dictionary[groupTypeString]) {
        throw new Error(`Тип группы ${groupTypeString} не поддерживается`);
    }

    return dictionary[groupTypeString];
}

export function mapGroupTypeToAdvType(groupType: GroupType): AdvType {
    const dictionary = {
        [GroupType.CPM_AUDIO]: AdvType.CPM_AUDIO,
        [GroupType.CPM_VIDEO]: AdvType.CPM_VIDEO,
        [GroupType.CPM_OUTDOOR]: AdvType.CPM_OUTDOOR,
        [GroupType.CPM_INDOOR]: AdvType.CPM_INDOOR,
        [GroupType.CPM_GEOPRODUCT]: AdvType.CPM_GEOPRODUCT,
        [GroupType.INTERNAL]: AdvType.INTERNAL,
        [GroupType.PERFORMANCE]: AdvType.PERFORMANCE,
        [GroupType.TEXT]: AdvType.TEXT,
        [GroupType.MOBILE_CONTENT]: AdvType.MOBILE_CONTENT,
        [GroupType.MCBANNER]: AdvType.MCBANNER,
        [GroupType.CPM_BANNER]: AdvType.CPM_BANNER,
        [GroupType.DYNAMIC]: AdvType.DYNAMIC
    };

    if (!dictionary[groupType]) {
        throw new Error(`Тип группы ${groupType} не поддерживается`);
    }

    return dictionary[groupType];
}

export function mapRestrictionGroupRowTypeToGroupType(
    groupTypeString: AdGroupType
): GroupType {
    const dictionary = {
        [AdGroupType.CPM_AUDIO]: GroupType.CPM_AUDIO,
        [AdGroupType.CPM_VIDEO]: GroupType.CPM_VIDEO,
        [AdGroupType.CPM_BANNER]: GroupType.CPM_BANNER,
        [AdGroupType.CPM_INDOOR]: GroupType.CPM_INDOOR,
        [AdGroupType.CPM_OUTDOOR]: GroupType.CPM_OUTDOOR,
        [AdGroupType.CPM_GEOPRODUCT]: GroupType.CPM_GEOPRODUCT,
        [AdGroupType.CPM_YNDX_FRONTPAGE]: GroupType.CPM_YNDX_FRONTPAGE
    };

    if (!dictionary[groupTypeString]) {
        throw new Error(`Тип группы ${groupTypeString} не поддерживается`);
    }

    return dictionary[groupTypeString];
}

export function mapCampaignRowPlatformToCampaignPlatform(
    platform: GdCampaignPlatform
): CampaignPlatform {
    return castStringToEnum(platform, CampaignPlatform);
}

export function mapStatsDataToStats(statsData: statsDataFragment): Stats {
    return {
        ctr: getNumberValue(statsData.ctr),
        avgCpmCost: getNumberValue(statsData.cpmPrice),
        profitability: getNumberValue(statsData.profitability),
        cost: getNumberValue(statsData.cost),
        avgClickPosition: getValueFromApiFormat(statsData.avgClickPosition),
        conversionRate: getNumberValue(statsData.conversionRate),
        bounceRate: getNumberValue(statsData.bounceRate),
        revenue: getNumberValue(statsData.revenue),
        avgClickCost: getNumberValue(statsData.avgClickCost),
        avgShowPosition: getValueFromApiFormat(statsData.avgShowPosition),
        avgDepth: getValueFromApiFormat(statsData.avgDepth),
        clicks: getNumberValue(statsData.clicks),
        avgGoalCost: getNumberValue(statsData.avgGoalCost),
        goals: getNumberValue(statsData.goals),
        shows: getNumberValue(statsData.shows)
    };
}

export function mapDateRangePresetToApiFormat(
    dateRange: DateRangePreset
): GdStatPresetInput {
    const dateRangePresetsDictionary = {
        [DateRangePreset.today]: GdStatPresetInput.TODAY,
        [DateRangePreset.yesterday]: GdStatPresetInput.YESTERDAY,
        [DateRangePreset.currentWeek]: GdStatPresetInput.CURRENT_WEEK,
        [DateRangePreset.previousWeek]: GdStatPresetInput.PREVIOUS_WEEK,
        [DateRangePreset.last7Days]: GdStatPresetInput.LAST_7DAYS,
        [DateRangePreset.currentMonth]: GdStatPresetInput.CURRENT_MONTH,
        [DateRangePreset.previousMonth]: GdStatPresetInput.PREVIOUS_MONTH,
        [DateRangePreset.last30Days]: GdStatPresetInput.LAST_30DAYS
    };

    if (!dateRangePresetsDictionary[dateRange]) {
        throw new Error(`Для значения ${dateRange} не определено название`);
    }

    return dateRangePresetsDictionary[dateRange];
}

export function mapDateRangePresetToDatePeriod(
    refDate: moment.Moment,
    dateRange: DateRangePreset
): { dateFrom: moment.Moment; dateTo: moment.Moment } {
    const dateRangePresetsPeriodValues = {
        [DateRangePreset.today]: {
            dateFrom: refDate.clone(),
            dateTo: refDate.clone()
        },
        [DateRangePreset.yesterday]: (function(): {
            dateFrom: moment.Moment;
            dateTo: moment.Moment;
        } {
            const yesterday = refDate.clone().subtract(1, 'day');

            return { dateFrom: yesterday, dateTo: yesterday };
        })(),
        [DateRangePreset.currentWeek]: {
            dateFrom: refDate.clone().weekday(0),
            dateTo: refDate
        },
        [DateRangePreset.previousWeek]: {
            dateFrom: refDate
                .clone()
                .weekday(0)
                .weekday(-7),
            dateTo: refDate
                .clone()
                .weekday(0)
                .weekday(-1)
        },
        [DateRangePreset.last7Days]: {
            dateFrom: refDate.clone().subtract(7, 'day'),
            dateTo: refDate.clone()
        },
        [DateRangePreset.currentMonth]: {
            dateFrom: refDate.clone().startOf('month'),
            dateTo: refDate.clone()
        },
        [DateRangePreset.previousMonth]: {
            dateFrom: refDate
                .clone()
                .subtract(1, 'month')
                .startOf('month'),
            dateTo: refDate
                .clone()
                .subtract(1, 'month')
                .endOf('month')
        },
        [DateRangePreset.last30Days]: {
            dateFrom: refDate.clone().subtract(30, 'day'),
            dateTo: refDate.clone()
        },
        [DateRangePreset.last90Days]: {
            dateFrom: refDate.clone().subtract(90, 'day'),
            dateTo: refDate.clone()
        },
        [DateRangePreset.last365Days]: {
            dateFrom: refDate.clone().subtract(365, 'day'),
            dateTo: refDate.clone()
        },
        [DateRangePreset.allTime]: {
            dateFrom: refDate.clone().subtract(10, 'years'),
            dateTo: refDate.clone()
        }
    };

    if (!dateRangePresetsPeriodValues[dateRange]) {
        throw new Error(`Для значения ${dateRange} не определен период`);
    }

    return dateRangePresetsPeriodValues[dateRange];
}

export function mapSortParamsToString(
    sortParams?: SortParams
): {
    order: string;
    field: string;
    params?: {
        goalId?: string;
    };
}[] {
    if (!sortParams) {
        // Сортировка поумолчанию
        return [];
    }

    const { direction, key, goalId } = sortParams;
    const paramsObj = goalId
        ? {
              params: {
                  goalId
              }
          }
        : {};

    return [
        {
            order: direction.toLocaleUpperCase(),
            field: key.toLocaleUpperCase(),
            ...paramsObj
        }
    ];
}

export function mapBudgetModeToApiFormat(
    budgetMode: GdCampaignBudgetShowMode
): string {
    const dictionary = {
        [GdCampaignBudgetShowMode.STRETCHED]: 'stretched',
        [GdCampaignBudgetShowMode.DEFAULT]: 'default'
    };

    if (!dictionary[budgetMode]) {
        throw new Error(
            `Для значения ${budgetMode} не определено api значение`
        );
    }

    return dictionary[budgetMode];
}

/* Возвращает cmd для массового действия над элементами грида */
export function mapListActionToApiFormat(action: GridAction): string {
    const dictionary = {
        [CampaignsAction.resume]: 'resumeCamp',
        [CampaignsAction.stop]: 'stopCamp',
        [CampaignsAction.archive]: 'campArc',
        [CampaignsAction.unarchive]: 'campUnarc',
        [CampaignsAction.remoderate]: 'remoderateCamp',
        [CampaignsAction.copy]: 'copyCampClient',
        [CampaignsAction.delete]: 'delCamp',
        [CampaignsAction.removeFromFavorites]: 'unFavoriteCamp',
        [CampaignsAction.addToFavorites]: 'favoriteCamp',

        [GroupsAction.resume]: 'resumeAdGroup',
        [GroupsAction.stop]: 'stopAdGroup',
        [GroupsAction.delete]: 'deleteAdGroups',
        [GroupsAction.sendBK]: 'admAdGroupsSendBK',
        [GroupsAction.archive]: 'archiveBanner',
        [GroupsAction.copy]: 'copy',
        [GroupsAction.unarchive]: 'unarchiveBanner',
        [GroupsAction.remoderateBannersAdditions]: 'dnaRemoderateAdsAdditions',
        [GroupsAction.moderateAcceptBannersAdditions]:
            'dnaModerateAcceptAdsAdditions',
        [GroupsAction.admSendMD]: 'dnaAdmSendMD',
        [GroupsAction.sendModerateByAgency]: 'dnaSendModerate',
        [GroupsAction.sendModerateByClient]: 'dnaClientSendModerate',
        [GroupsAction.moderateAccept]: 'dnaModerateAccept',

        [BannersAction.archive]: 'archiveBanners',
        [BannersAction.unarchive]: 'unarchiveBanners',
        [BannersAction.resume]: 'resumeBanner',
        [BannersAction.stop]: 'stopBanner',
        [BannersAction.delete]: 'deleteBanner',
        [BannersAction.editPrices]: 'editPrices'
    };

    if (!dictionary[action]) {
        throw new Error(`Для действия ${action} не определен метод Api`);
    }

    return dictionary[action];
}

export function mapTargetingModeToApiFormat(
    targetingMode: GdAdditionalTargetingMode
): GdAdditionalTargetingModeInput {
    const dictionary = {
        [GdAdditionalTargetingMode.FILTERING]:
            GdAdditionalTargetingModeInput.FILTERING,
        [GdAdditionalTargetingMode.TARGETING]:
            GdAdditionalTargetingModeInput.TARGETING
    };

    if (!dictionary[targetingMode]) {
        throw new Error(
            `Для значения ${targetingMode} не определено api значение`
        );
    }

    return dictionary[targetingMode];
}

export function mapJoinTypeToApiFormat(
    joinType: GdAdditionalTargetingJoinType
): GdAdditionalTargetingJoinTypeInput {
    const dictionary = {
        [GdAdditionalTargetingJoinType.ALL]:
            GdAdditionalTargetingJoinTypeInput.ALL,
        [GdAdditionalTargetingJoinType.ANY]:
            GdAdditionalTargetingJoinTypeInput.ANY
    };

    if (!dictionary[joinType]) {
        throw new Error(`Для значения ${joinType} не определено api значение`);
    }

    return dictionary[joinType];
}

export const mapApiWalletBudgetToWalletBudget = (budget: {
    sum: string | null;
    showMode: GdWalletBudgetShowMode | null;
    type: GdWalletBudgetType;
}): WalletDayBudgetValue => {
    return {
        sum: budget.sum ? Number(budget.sum) : undefined,
        mode: getValueFromApiFormat(budget.showMode),
        type: budget.type
    };
};

export function getHrefForDomainFromApiFormat(
    href: string | null
): string | undefined {
    return href ? '//' + href : undefined;
}

export const mapApiWalletsToWalletData = (
    wallets: Array<WalletsDataFragment>
): WalletData => {
    const wallet =
        wallets.find(
            item =>
                item.status.enabled && item.currency !== CurrencyCode.YND_FIXED
        ) || wallets[0];

    const walletBudget = wallet
        ? mapApiWalletBudgetToWalletBudget(wallet.budget)
        : undefined;
    const walletActions = wallet ? wallet.actions : [];

    return {
        id: wallet ? wallet.id : undefined,
        walletActions,
        walletBudget,
        sum: wallet ? Number(wallet.sum) : undefined,
        currency: wallet ? wallet.currency : CurrencyCode.YND_FIXED
    };
};

const mapUserAccountInfo = (
    acconut: FetchUserAccountInfoRequest
): UserAccountInfo => {
    const { uid, login, displayName, defaultEmail } = acconut;

    return {
        uid,
        login,
        displayName: {
            name: displayName.name,
            avatar: displayName.default_avatar
        },
        defaultEmail
    };
};

export const mapUserAccountsInfo = (
    data: FetchUserAccountsInfoRequest
): UserAccountsInfo => {
    const accounts = data.accounts;

    if (!accounts) {
        return {};
    }

    const currentAccount = accounts.find(({ uid }) => uid === data.default_uid);

    return {
        data: accounts.map(mapUserAccountInfo),
        current: mapUserAccountInfo(
            currentAccount as FetchUserAccountInfoRequest
        )
    };
};

export function mapTotalStatsToApiFormat(totalStats: {
    cost: string | null;
    shows: string | null;
    clicks: string | null;
    ctr: string | null;
}): GridTotalStatsInfo {
    return keys(totalStats).reduce(
        (acc, key) => {
            return { ...acc, [key]: getNumberValue(totalStats[key]) };
        },
        {} as GridTotalStatsInfo
    );
}

export const mapAdditionalFilterToApiFormat = (
    additionalFilterType: ScreenAdditionalFilter
): boolean | undefined => {
    switch (additionalFilterType) {
        case CampaignsAdditionalFilter.ALL_ARCHIVED:
        case GroupsAdditionalFilter.ALL_ARCHIVED:
        case BannersAdditionalFilter.ALL_ARCHIVED:
            return true;
        case CampaignsAdditionalFilter.ALL_ACTIVE:
        case GroupsAdditionalFilter.ALL_ACTIVE:
        case BannersAdditionalFilter.ALL_ACTIVE:
            return false;
        case CampaignsAdditionalFilter.ALL:
        case GroupsAdditionalFilter.ALL:
        case BannersAdditionalFilter.ALL:
            return undefined;
        default:
            throw `неподдерживаемый ScreenTitleFilterType: ${additionalFilterType}`;
    }
};

export const mapCampaignsCalcTypeFilterToApiFormat = (
    campaignsPaymentsFilter: CampaignsCalcTypeFilter
): GdCalcTypeInput | undefined => {
    switch (campaignsPaymentsFilter) {
        case CampaignsCalcTypeFilter.CPC:
            return GdCalcTypeInput.CPC;
        case CampaignsCalcTypeFilter.CPM:
            return GdCalcTypeInput.CPM;
        default:
            return undefined;
    }
};

export const mapAuthorizationData = (
    data: AuthorizationQuery
): AuthorizationData | undefined => {
    if (!data.operator || !data.operator.chiefInfo) {
        return undefined;
    }
    return {
        id: data.operator.chiefInfo.clientId,
        login: data.operator.chiefInfo.login,
        isFreelancer: data.operator.isFreelancer,
        userId: data.operator.chiefInfo.userId
    };
};

export const mapCommonResponseToAccessRights = (
    data: CommonQuery
): AccessRights => {
    const { client, operator } = data;

    const result = omit(
        {
            ...client.access,
            ...operator.access
        },
        ['__typename']
    );

    return {
        ...(result as Exclude<AccessRights, '__typename'>),
        canCreateCampaignTypes: client.access.canCreateCampaignTypes || []
    };
};

export const mapApiClientToClientData = (
    client: CommonQuery_client,
    login: string
): ClientProperties => {
    const { __typename, id, agencyInfo, ...clientData } = client.info;

    return {
        ...clientData,
        agencyInfo: mapAgencyInfoToClientData(agencyInfo) as AgencyInfo,
        ClientID: id,
        login
    };
};

const mapAgencyInfoToClientData = (
    agencyInfo: CommonQuery_client_info_agencyInfo | null
): AgencyInfo | undefined => {
    if (agencyInfo === null) {
        return undefined;
    }

    const { __typename, ...agencyInfoFromApi } = agencyInfo;

    return mapValues(agencyInfoFromApi, value => getValueFromApiFormat(value));
};

export const mapOfficeContactsApiData = (
    officeData: OfficeApiContacts
): OfficeInfo => {
    const office = officeData.client || officeData.support;
    const officeInfo: OfficeInfo = { regionContacts: [] };
    if (office) {
        try {
            // этот try-catch нужен, так как тут парсятся пропсы, которые передаются
            // из большого директа, и оказалось, что иногда они не соответствую тайпингам
            // https://st.yandex-team.ru/DIRECT-95867

            if (office.landline) {
                const {
                    landline: [[landlinePhone, , landlineDescription]]
                } = office;

                officeInfo.cityContact = {
                    description: landlineDescription,
                    phone: landlinePhone
                };
            }

            if (office.regional) {
                const {
                    regional: [[regionalPhone, , regionalDescription]]
                } = office;

                const regionContact = {
                    description: regionalDescription,
                    phone: regionalPhone
                };

                officeInfo.regionContacts = [regionContact];
            }
        } catch (e) {
            return officeInfo;
        }
    }
    return officeInfo;
};

export function mapOfficeContacts(
    officeContacts: OfficeApiContact
): OfficeContact {
    return {
        description: officeContacts.description,
        cityName: getValueFromApiFormat(officeContacts.cityName),
        phone: officeContacts.phone,
        phoneExtension: getValueFromApiFormat(officeContacts.phoneExtension)
    };
}

export function mapRegionContacts(
    officeContacts: Array<OfficeApiContact | null>
): OfficeContact[] {
    return officeContacts.filter(Boolean).map(mapOfficeContacts);
}
